import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useRestaurant } from '../../context/RestaurantContext';
import { 
  Gift, Settings, Users, TrendingUp, Star, Coins,
  Save, RefreshCw, Info, Calculator
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { toast } from 'sonner';

export default function LoyaltyProgram() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { selectedRestaurant } = useRestaurant();
  
  const [config, setConfig] = useState({
    enabled: false,
    points_per_pound: 10,
    points_to_pound: 100,
    min_points_redeem: 100,
    max_discount_percent: 50,
    welcome_bonus: 0,
    birthday_bonus: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');

  useEffect(() => {
    if (selectedRestaurant) {
      fetchConfig();
    }
  }, [selectedRestaurant]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/restaurants/${selectedRestaurant.id}/loyalty/config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch loyalty config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put(`/restaurants/${selectedRestaurant.id}/loyalty/config`, config);
      toast.success('Loyalty settings saved');
    } catch (error) {
      console.error('Failed to save config:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  // Calculate example values for display
  const exampleSpend = 50; // £50 order
  const pointsEarned = exampleSpend * config.points_per_pound;
  const ordersToRedeem = Math.ceil(config.min_points_redeem / pointsEarned);
  const discountValue = config.min_points_redeem / config.points_to_pound;

  if (!selectedRestaurant) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-slate-500">No restaurant selected</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="loyalty-program">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Loyalty Program</h1>
          <p className="text-slate-500">Configure rewards for your customers</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge 
            className={config.enabled ? 'bg-green-500' : 'bg-slate-400'}
          >
            {config.enabled ? 'Active' : 'Inactive'}
          </Badge>
          <Button onClick={handleSave} disabled={saving || loading}>
            <Save className={`w-4 h-4 mr-2 ${saving ? 'animate-spin' : ''}`} />
            Save Settings
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-slate-200 pb-2">
        <Button
          variant={activeTab === 'settings' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('settings')}
        >
          <Settings className="w-4 h-4 mr-2" />
          Settings
        </Button>
        <Button
          variant={activeTab === 'preview' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('preview')}
        >
          <Calculator className="w-4 h-4 mr-2" />
          Preview
        </Button>
      </div>

      {activeTab === 'settings' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Enable/Disable Card */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-orange-500" />
                Program Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Enable Loyalty Program</p>
                  <p className="text-sm text-slate-500">
                    Customers will earn points on every purchase
                  </p>
                </div>
                <Switch
                  checked={config.enabled}
                  onCheckedChange={(checked) => handleChange('enabled', checked)}
                  data-testid="loyalty-toggle"
                />
              </div>
            </CardContent>
          </Card>

          {/* Points Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Coins className="w-5 h-5 text-yellow-500" />
                Earning Points
              </CardTitle>
              <CardDescription>
                Configure how customers earn points
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Points per £1 spent
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="w-4 h-4 text-slate-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>How many points customers earn for each pound spent</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <Input
                  type="number"
                  value={config.points_per_pound}
                  onChange={(e) => handleChange('points_per_pound', parseInt(e.target.value) || 0)}
                  min="1"
                  max="100"
                  data-testid="points-per-pound"
                />
                <p className="text-xs text-slate-500">
                  Example: A £50 order earns {50 * config.points_per_pound} points
                </p>
              </div>

              <div className="space-y-2">
                <Label>Welcome Bonus (First Order)</Label>
                <Input
                  type="number"
                  value={config.welcome_bonus}
                  onChange={(e) => handleChange('welcome_bonus', parseInt(e.target.value) || 0)}
                  min="0"
                  data-testid="welcome-bonus"
                />
                <p className="text-xs text-slate-500">
                  Bonus points given on customer's first order
                </p>
              </div>

              <div className="space-y-2">
                <Label>Birthday Bonus</Label>
                <Input
                  type="number"
                  value={config.birthday_bonus}
                  onChange={(e) => handleChange('birthday_bonus', parseInt(e.target.value) || 0)}
                  min="0"
                  data-testid="birthday-bonus"
                />
                <p className="text-xs text-slate-500">
                  Bonus points given on customer's birthday
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Redemption Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-purple-500" />
                Redeeming Points
              </CardTitle>
              <CardDescription>
                Configure how customers redeem points
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Points per £1 discount
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="w-4 h-4 text-slate-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>How many points needed for £1 off</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <Input
                  type="number"
                  value={config.points_to_pound}
                  onChange={(e) => handleChange('points_to_pound', parseInt(e.target.value) || 1)}
                  min="1"
                  data-testid="points-to-pound"
                />
                <p className="text-xs text-slate-500">
                  Example: {config.points_to_pound} points = £1 discount
                </p>
              </div>

              <div className="space-y-2">
                <Label>Minimum Points to Redeem</Label>
                <Input
                  type="number"
                  value={config.min_points_redeem}
                  onChange={(e) => handleChange('min_points_redeem', parseInt(e.target.value) || 0)}
                  min="0"
                  step="10"
                  data-testid="min-points"
                />
                <p className="text-xs text-slate-500">
                  Minimum = £{(config.min_points_redeem / config.points_to_pound).toFixed(2)} discount
                </p>
              </div>

              <div className="space-y-2">
                <Label>Maximum Discount (%)</Label>
                <Input
                  type="number"
                  value={config.max_discount_percent}
                  onChange={(e) => handleChange('max_discount_percent', parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                  data-testid="max-discount"
                />
                <p className="text-xs text-slate-500">
                  Maximum discount as percentage of order total
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'preview' && (
        <div className="space-y-6">
          {/* Example Calculator */}
          <Card>
            <CardHeader>
              <CardTitle>How It Works - Customer View</CardTitle>
              <CardDescription>
                Preview how your loyalty program appears to customers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Earn */}
                <div className="text-center p-6 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl">
                  <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Coins className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">Earn Points</h3>
                  <p className="text-3xl font-bold text-orange-600 mb-2">
                    {config.points_per_pound} pts
                  </p>
                  <p className="text-slate-600">for every £1 spent</p>
                </div>

                {/* Collect */}
                <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
                  <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">Collect</h3>
                  <p className="text-3xl font-bold text-purple-600 mb-2">
                    {config.min_points_redeem} pts
                  </p>
                  <p className="text-slate-600">minimum to redeem</p>
                </div>

                {/* Redeem */}
                <div className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Gift className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">Redeem</h3>
                  <p className="text-3xl font-bold text-green-600 mb-2">
                    £{discountValue.toFixed(2)}
                  </p>
                  <p className="text-slate-600">discount off your order</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Example Journey */}
          <Card>
            <CardHeader>
              <CardTitle>Customer Journey Example</CardTitle>
              <CardDescription>
                Based on a £{exampleSpend} average order value
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order #</TableHead>
                    <TableHead>Order Total</TableHead>
                    <TableHead>Points Earned</TableHead>
                    <TableHead>Total Balance</TableHead>
                    <TableHead>Can Redeem?</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[1, 2, 3, 4, 5].map((orderNum) => {
                    const totalPoints = orderNum * pointsEarned + (orderNum === 1 ? config.welcome_bonus : 0);
                    const canRedeem = totalPoints >= config.min_points_redeem;
                    
                    return (
                      <TableRow key={orderNum}>
                        <TableCell className="font-medium">Order {orderNum}</TableCell>
                        <TableCell>£{exampleSpend.toFixed(2)}</TableCell>
                        <TableCell>
                          {pointsEarned} pts
                          {orderNum === 1 && config.welcome_bonus > 0 && (
                            <span className="text-green-600 ml-1">
                              +{config.welcome_bonus} bonus
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="font-bold">{totalPoints} pts</TableCell>
                        <TableCell>
                          {canRedeem ? (
                            <Badge className="bg-green-500">
                              Yes! £{(totalPoints / config.points_to_pound).toFixed(2)} available
                            </Badge>
                          ) : (
                            <Badge variant="outline">
                              Need {config.min_points_redeem - totalPoints} more
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong>Summary:</strong> With your current settings, a customer spending £{exampleSpend} per visit 
                  will need <strong>{ordersToRedeem} order{ordersToRedeem > 1 ? 's' : ''}</strong> to earn 
                  their first £{discountValue.toFixed(2)} discount.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
